/* eslint-disable complexity */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TableRow, TableCell, Button, CircularProgress } from '@mui/material';
import { getParameterFromValue } from '@scale/shared/corp/ruleEngine';
import { ExecutionStatus, IExecutionParameter } from '@scale/shared/types/execution';
import { IQueryExecution } from '@scale/shared/types/query';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { makeStyles } from 'design-system/styles';
import cs from 'classnames';
import ExecutionParameter from 'react-scale/corp/components/RuleEngine/ExecutionParameter';
import QueryResult from 'react-scale/corp/components/RuleEngine/QueryResult';
import CodeMirror from 'react-scale/corp/components/CodeMirror';
import { IStepExecution } from '@scale/shared/types/ruleEngine/step';
import { IRuleExecution } from '@scale/shared/types/ruleEngine/rule';
import { Link } from '@scale/scaleui-radix';
import CopyIDIcon from 'react-scale/corp/views/CorpProjectGroupManager/components/CopyIDIcon';
import ConfirmationDialog from 'react-scale/corp/views/CorpProjectGroupManager/components/ConfirmationDialog';

function ExecutionRow(props: {
  execution: (IStepExecution | IRuleExecution) & { _id: string };
  children?: JSX.Element[];
  label?: string;
  auditExecution?: (approved: boolean) => Promise<void>;
  displayUtcTime?: boolean;
  stopRuleExecution?: () => Promise<void>;
  isStoppingRuleExecution?: boolean;
}) {
  const {
    execution,
    auditExecution,
    children,
    label,
    displayUtcTime,
    stopRuleExecution,
    isStoppingRuleExecution,
  } = props;
  const classes = styles({ status: execution.status });

  const requiresApproval = useMemo(() => {
    return Boolean(
      auditExecution && execution.status === ExecutionStatus.Pending && execution.requiresApproval,
    );
  }, [auditExecution, execution]);

  const [dateLabel, hourLabel] = useMemo(() => {
    const startedAt = moment(execution.startedAt);
    if (displayUtcTime) {
      startedAt.utc();
    }
    return [moment(startedAt).format('YYYY-MM-DD'), moment(startedAt).format('HH:mm')];
  }, [execution, displayUtcTime]);

  const rowLabel: string | undefined = useMemo(
    () => ('tool' in execution && execution.tool ? execution.tool : label),
    [label, execution],
  );

  const [open, setOpen] = useState<boolean>(requiresApproval);
  const [loading, setLoading] = useState<boolean>(false);

  async function onAuditExecution(approve: boolean) {
    if (auditExecution) {
      setLoading(true);
      await auditExecution(approve);
    }
    setLoading(false);
  }

  const durationLabel = useMemo(() => {
    const startedAt = moment(execution.startedAt);
    const difference = moment(execution.finishedAt ?? new Date()).diff(startedAt);
    const duration = moment.duration(difference);

    const hour = duration.hours();
    duration.subtract(moment.duration(hour, 'hours'));

    const minute = duration.minutes();
    duration.subtract(moment.duration(minute, 'minutes'));

    const second = duration.seconds();
    duration.subtract(moment.duration(second, 'seconds'));

    const millisecond = duration.milliseconds();

    const time = {
      hour,
      minute,
      second,
      millisecond,
    };

    return Object.keys(time)
      .filter(label => label in time && time[label as keyof typeof time])
      .slice(0, 2)
      .map(label => {
        const value = time[label as keyof typeof time];
        return value ? `${value} ${label}${value > 1 ? 's' : ''}` : undefined;
      })
      .join(', ');
  }, [execution]);

  const parameterTypes = useMemo(() => {
    if (execution.parameters) {
      const types: { [parameterKey: string]: IExecutionParameter } = {};
      Object.keys(execution.parameters).forEach(parameterKey => {
        const parameter = execution.parameters![parameterKey];
        const parameterType = getParameterFromValue(parameter);
        if (parameterType) {
          types[parameterKey] = parameterType;
        }
      });
      return types;
    }
    return {};
  }, [execution]);

  const statusBackground = useMemo(() => {
    switch (execution.status) {
      case ExecutionStatus.Completed:
        return '#27cf83';
      case ExecutionStatus.Errored:
        return 'red';
      case ExecutionStatus.Running:
        return '#005eff';
      case ExecutionStatus.Pending:
        return '#8c00ff';
      default:
        return 'grey';
    }
  }, [execution]);

  return (
    <>
      <TableRow
        className={children ? undefined : cs(classes.executionStepItemRow, classes.noBorderRow)}
      >
        <TableCell style={{ padding: 0, width: '33px' }}>
          <button className={classes.tableButton} onClick={() => setOpen(prev => !prev)}>
            <FontAwesomeIcon icon={`chevron-${open ? 'up' : 'down'}`} />
          </button>
        </TableCell>
        <TableCell
          style={{
            height: '34px',
            maxWidth: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <span
            style={
              open
                ? {}
                : {
                    whiteSpace: 'nowrap',
                  }
            }
          >
            {'rule' in execution ? (
              <a
                href={`/corp/qm/queue/${execution.project}/rules/${execution.rule}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {rowLabel}
              </a>
            ) : (
              rowLabel
            )}
          </span>
        </TableCell>
        <TableCell
          style={{ whiteSpace: 'nowrap' }}
          component="th"
          scope="row"
          align="right"
          width={110}
        >
          {dateLabel}
        </TableCell>
        <TableCell component="th" scope="row" style={{ paddingLeft: 0 }} width={50}>
          {hourLabel}
        </TableCell>
        <TableCell width={250}>{durationLabel}</TableCell>
        <TableCell style={{ padding: '6px' }} width={120}>
          <div
            style={{
              textTransform: 'capitalize',
              textAlign: 'center',
              fontWeight: 'bold',
              borderRadius: '2rem',
              ...(children
                ? {
                    background: statusBackground,
                    color: 'white',
                  }
                : {}),
            }}
          >
            {execution.status}
          </div>
        </TableCell>
        <TableCell
          style={{ whiteSpace: 'nowrap' }}
          component="th"
          scope="row"
          align="right"
          width={110}
        >
          {execution._id}
        </TableCell>
        <TableCell
          style={{ whiteSpace: 'nowrap' }}
          component="th"
          scope="row"
          align="right"
          width={110}
        >
          {execution.status === ExecutionStatus.Running && stopRuleExecution ? (
            <ConfirmationDialog
              disableTrigger={isStoppingRuleExecution}
              onAccept={() => stopRuleExecution?.()}
              title="Stop Rule Execution"
              bodyText={'Are you sure you want to stop this rule execution?'}
              trigger={
                <Button
                  disabled={isStoppingRuleExecution}
                  variant="contained"
                  color="error"
                  style={{ textTransform: 'capitalize' }}
                >
                  {isStoppingRuleExecution ? 'Stopping...' : 'Stop'}
                </Button>
              }
            />
          ) : (
            '-'
          )}
        </TableCell>
      </TableRow>
      {!children && open && (
        <>
          <TableRow className={classes.executionStepItemRow}>
            <TableCell
              colSpan={6}
              style={{
                padding: 0,
              }}
            >
              {(execution as IStepExecution).snowflakeQueryId ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '4px',
                    alignItems: 'center',
                    marginLeft: '16px',
                  }}
                >
                  <Link
                    href={`https://app.snowflake.com/scale/pxa65918/#/compute/history/queries/${
                      (execution as IStepExecution).snowflakeQueryId
                    }/detail`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {`Snowflake Query Id: ${(execution as IStepExecution).snowflakeQueryId}`}
                  </Link>
                  {/* The toast here doesn't gets triggered cause corp doesn't have a toast provider. Didn't want to add it just for this */}
                  {/* If we continue to work on this probably we will move it to ops hub anyways */}
                  <CopyIDIcon
                    id={(execution as IStepExecution).snowflakeQueryId as string}
                    size="sm"
                  />
                </div>
              ) : null}
              {(execution as IQueryExecution).content ? (
                <div
                  style={{
                    borderRadius: '8px',
                    background: 'white',
                    margin: '0 16px 16px 16px',
                    position: 'relative',
                    overflow: 'auto',
                    maxHeight: '15vh',
                  }}
                >
                  <CodeMirror
                    language="sql"
                    readOnly
                    lineWrapping
                    value={(execution as IQueryExecution).content}
                  />
                </div>
              ) : (
                execution.parameters &&
                Object.keys(execution.parameters).length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'start',
                      alignItems: 'start',
                      gap: '.7rem',
                      padding: '.7rem',
                      borderRadius: '8px',
                      background: 'white',
                      margin: '0 16px 16px 16px',
                    }}
                  >
                    {Object.keys(parameterTypes).map(parameterKey => {
                      const parameter = parameterTypes[parameterKey];
                      return (
                        <ExecutionParameter
                          key={parameterKey}
                          parameterKey={parameterKey}
                          parameter={parameter}
                        />
                      );
                    })}
                  </div>
                )
              )}
              {!execution.error && Array.isArray(execution.output) ? (
                <div
                  style={{
                    gap: '.7rem',
                    padding: '.7rem',
                    borderRadius: '8px',
                    background: 'white',
                    display: 'flex',
                    margin: '0 16px 16px 16px',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    position: 'relative',
                    overflow: 'auto',
                    height: '15vh',
                  }}
                >
                  <div style={{ position: 'absolute' }}>
                    <QueryResult result={execution as IQueryExecution} />
                  </div>
                </div>
              ) : (
                ((execution.error && Object.keys(execution.error).length > 0) ||
                  (execution.output && Object.keys(execution.output).length > 0)) && (
                  <div
                    style={{
                      padding: '.7rem',
                      borderRadius: '8px',
                      background: 'white',
                      maxHeight: '20vh',
                      overflow: 'auto',
                      margin: '0 16px 16px 16px',
                    }}
                  >
                    {JSON.stringify(execution.error || execution.output)}
                  </div>
                )
              )}
              {requiresApproval && (
                <div
                  style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: loading ? 'center' : 'start',
                    margin: '0 16px 16px 16px',
                  }}
                >
                  {loading ? (
                    <CircularProgress />
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => onAuditExecution(true)}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => onAuditExecution(false)}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                </div>
              )}
            </TableCell>
          </TableRow>
        </>
      )}
      {open && children && children.length > 0 && children}
    </>
  );
}

const styles = makeStyles<{ status?: ExecutionStatus }>({
  tableButton: {
    background: 'none',
    border: 'none',
    height: '33px',
    width: '33px',
    cursor: 'pointer',
    borderRadius: '2rem',
    '&:hover': {
      background: 'lightgrey',
    },
  },
  executionStepItemRow: props => ({
    '& .MuiTableCell-root': {
      background: () => {
        switch (props.status) {
          case ExecutionStatus.Completed:
            return '#e8fcf6';
          case ExecutionStatus.Errored:
            return '#fcebe8';
          case ExecutionStatus.Running:
            return '#e6efff';
          case ExecutionStatus.Pending:
            return '#f5ebff';
          default:
            return '#f7f7f7';
        }
      },
    },
  }),
  noBorderRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
});

export default ExecutionRow;
